import {
  Box,
  Typography,
  useTheme,
  LinearProgress,
  Pagination,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import useStyles from "./styles";
import handleColors from "../../utils/handleColors";
import getColorsForScheme from "../../../legends/utils/getColorsForScheme";
import TooltipMui from "../../../tooltipMui";
import { MyLinearProgressConfig } from "./types";
import maskUnits from "../../../../utils/maskUnits";
import { darken } from "@mui/system";
import { getColorsArray } from "../../../../utils/getColorsPallet";
const formatSeries = (data: MyDatum[], indexBy: string, keys: string[]) =>
  data.map((item) => ({
    label: item[indexBy],
    value: item[keys[0]],
  }));

const CustomLinearProgress: React.FC<MyLinearProgressConfig> = (props) => {
  const {
    data,
    colorsByKey,
    keys,
    indexBy,
    colors,
    orientation,
    units,
    fractionDigits,
  } = props;

  const [boxHeight, setBoxHeight] = useState<number | null>(null);
  const [boxWidth, setBoxWidth] = useState<number | null>(null);
  const [isCompact, setIsCompact] = useState<boolean>(false);

  const linearProgRef = React.useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const styles = useStyles(theme);

  useMemo(() => {
    setIsCompact(verifyCompact(boxWidth));
  }, [boxWidth]);

  useEffect(() => {
    if (linearProgRef.current) {
      const { offsetHeight, offsetWidth } = linearProgRef.current;
      setBoxHeight(offsetHeight);
      setBoxWidth(offsetWidth);
    }
  }, [
    linearProgRef.current,
    linearProgRef.current?.offsetWidth,
    linearProgRef.current?.offsetHeight,
  ]);

  function verifyCompact(width: number | null) {
    return width !== null && width < 235 ? true : false;
  }

  function maxItemsPage(height: number | null, total: number) {
    if (height) {
      if (Math.ceil(height - 12) >= Math.ceil(total * 50)) {
        return Math.ceil((height - 12) / 50);
      }
      return Math.ceil((height - 115) / 50);
    }
    return 5;
  }

  const formattedData =
    keys && keys.length > 0 ? formatSeries(data, indexBy, keys) : [];

  // const dataKeys = Object.keys(data);
  // const transformedData = data.map((item) => ({
  //   value: item.value,
  //   label: item.label,
  // }));

  const [page, setPage] = useState(1);

  const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const orderedData = formattedData.sort(
    (a, b) => Number(b.value) - Number(a.value)
  );

  const itemsPerPage = maxItemsPage(boxHeight, formattedData.length);

  const totalPages = Math.ceil(formattedData.length / itemsPerPage);
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = orderedData.slice(startIndex, endIndex);
  const numItems = formattedData.length;
  const progressColors =
    (colors && getColorsArray(numItems, colors, false)) || [];
  const isPaginated = formattedData.length > itemsPerPage;

  return formattedData.length === 0 ? undefined : (
    <Box
      sx={styles.boxContainer(
        orientation === "horizontal" ? "row" : "column",
        isPaginated
      )}
      ref={linearProgRef}
    >
      {paginatedData.map((item, index) => {
        const globalIndex = startIndex + index;

        return (
          <Box sx={styles.boxStatus(isPaginated)} key={globalIndex + "boxs"}>
            <Box sx={styles.boxLinearProgress}>
              <Typography
                key={index + "text"}
                sx={{
                  color: "text.primary",
                  fontSize: 14,
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  mr: 1,
                }}
              >
                {item.label}
              </Typography>
              <Typography sx={{ color: "text.secondary" }}>
                {maskUnits(item.value, units, fractionDigits)}
              </Typography>
            </Box>
            <TooltipMui
              color={
                colorsByKey
                  ? handleColors(item.label, theme, colorsByKey)
                  : colors && progressColors
                  ? progressColors[globalIndex]
                  : undefined
              }
              valueX={item.label}
              valueZ={maskUnits(item.value, units, fractionDigits)}
            >
              <LinearProgress
                variant="buffer"
                value={
                  item.value
                    ? (Number(item.value) / Number(orderedData[0].value)) * 100
                    : 0
                }
                valueBuffer={100}
                sx={[
                  styles.linearProgress,
                  {
                    "& .MuiLinearProgress-bar1Buffer": {
                      backgroundColor: colorsByKey
                        ? handleColors(item.label, theme, colorsByKey)
                        : colors && progressColors
                        ? progressColors[globalIndex]
                        : undefined,
                      border: `1px solid ${
                        colorsByKey
                          ? darken(
                              handleColors(item.label, theme, colorsByKey),
                              0.2
                            )
                          : colors && progressColors
                          ? darken(progressColors[globalIndex] || "#000", 0.2)
                          : undefined
                      }`,
                    },
                  },
                ]}
              />
            </TooltipMui>
          </Box>
        );
      })}
      {isPaginated && (
        <Pagination
          sx={{
            width: boxWidth,
            flexShrink: 0,
            "& .MuiPagination-ul": {
              justifyContent: "center",
              flexWrap: "nowrap",
              ...(isCompact && {
                "& > li .MuiPaginationItem-page": {
                  display: "none",
                },
                "& > li .MuiPaginationItem-ellipsis": {
                  display: "none",
                },
                "& > li .Mui-selected": {
                  display: "flex",
                },
              }),
            },
            marginTop: "auto",
          }}
          count={isFinite(totalPages) ? totalPages : 5}
          page={page}
          onChange={handleChange}
          siblingCount={0}
        />
      )}
    </Box>
  );
};
export default CustomLinearProgress;
